import { useCallback, useState } from 'react'

import CheckIcon from '@/assets/icons/icon-check.svg'
import ExpandMoreIcon from '@/assets/icons/icon-expand-more.svg'
import { Button } from '@/components/ui/button.tsx'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import type { Creator } from '@/modules/creators/types'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar.tsx'
import { classNames } from '@/utils/helpers'

interface Props {
  selectable: Creator[]
  selected: Creator[]
  onAllSelect: () => void
  onAllRemove: () => void
  onAddSelect: (creator: Creator) => void
  onRemoveSelect: (creator: Creator) => void
  setSelect?: (creator: Creator[]) => void
  selectMultiple?: boolean
  showAvatar?: boolean
}

export const CreatorCombobox = ({
  selectable,
  selected,
  onAllSelect,
  onAllRemove,
  onAddSelect,
  onRemoveSelect,
  setSelect,
  selectMultiple = true,
  showAvatar = false,
}: Props) => {
  const [open, setOpen] = useState(false)
  const [isAllSelected, setAllSelected] = useState(
    selected.length === selectable.length,
  )

  const handleAllCreatorSelect = useCallback(() => {
    if (isAllSelected) {
      onAllRemove()
    } else {
      onAllSelect()
    }

    setAllSelected(!isAllSelected)
  }, [isAllSelected, onAllRemove, onAllSelect])

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>
        <Button
          //   variant="outline"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="flex justify-between"
        >
          <div className="flex gap-2 font-normal">
            {selectMultiple && selected.length === selectable.length ? (
              <span data-testid="all-creators-dropdown">All Creators</span>
            ) : selectMultiple && selected.length > 1 ? (
              <span> {selected.length} Creators selected</span>
            ) : selected.length >= 1 ? (
              <span>
                {
                  selectable.filter((c) =>
                    selected.some((s) => s.id === c.id),
                  )[0]?.name
                }
              </span>
            ) : (
              <span>
                {selectMultiple ? 'Select Creators' : 'Select Creator'}
              </span>
            )}
          </div>
          <ExpandMoreIcon className="w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandList>
            <CommandInput placeholder="Search Creator" />
            <CommandEmpty>No Creators found.</CommandEmpty>
            {selectMultiple && (
              <>
                <CommandGroup>
                  <CommandItem onSelect={handleAllCreatorSelect}>
                    <div
                      className={classNames(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary-500',
                        isAllSelected
                          ? 'bg-primary-500 text-white'
                          : 'opacity-50',
                      )}
                    >
                      {isAllSelected && <CheckIcon className="w-3" />}
                    </div>
                    All Creators
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup className=" max-h-44 overflow-auto">
              {selectable.map((creator, index) => {
                const isSelected =
                  selectMultiple || selected.length === 1
                    ? selected.includes(creator)
                    : index === 0

                return (
                  <CommandItem
                    key={creator.id}
                    className={
                      !selectMultiple && isSelected
                        ? 'bg-neutral-contrast/50'
                        : ''
                    }
                    onSelect={() => {
                      if (isSelected && selectMultiple) {
                        onRemoveSelect(creator)
                        setAllSelected(false)

                        return
                      }

                      if (selectMultiple) {
                        onAddSelect(creator)
                        if (selected.length === selectable.length - 1) {
                          setAllSelected(true)
                        }

                        return
                      }

                      if (setSelect) {
                        setSelect([creator])
                      }
                    }}
                  >
                    {selectMultiple && (
                      <div
                        className={classNames(
                          'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary-500',
                          isSelected
                            ? 'bg-primary-500 text-white'
                            : 'opacity-50',
                        )}
                      >
                        {isSelected && <CheckIcon className="w-3" />}
                      </div>
                    )}
                    {showAvatar && (
                      <CreatorAvatar
                        name={creator.name}
                        avatarSrc={creator.avatar?.croppedUrl}
                        className="mr-2 h-5 w-5"
                      />
                    )}
                    {creator.name}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
